import React, { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPlus, faEye, faPen, faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  Form,
  Row,
  Col,
  Card,
  Container,
  InputGroup,
  Button,
} from "react-bootstrap";

import { getAllEmployees, submitEmployee, downloadEmpDocument, getEmployeePhoto } from "../../../api/onboarddeboard";
import PersonalDetails from "./component/personalDetails/page";
import CommunicationDetails from "./component/communicationDetails/page";
import BankDetails from "./component/bankDetails/page";
import ServiceDetails from "./component/serviceDetails/page";
import DocumentDetails from "./component/documentDetails/page";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ─── Step config ─────────────────────────────────────────────────────────────
const steps = [
  { label: "Personal\nDetails" },
  { label: "Communication\nDetails" },
  { label: "Service\nDetails" },
  { label: "Bank\nDetails" },
  { label: "Documents" },
];

const avatarStyle = () => ({ bg: "#2E5CB81A", color: "#2E5CB8" });
const initials = (name) => {
  if (!name || typeof name !== "string" || name.trim() === "") return "?";
  return name.trim().split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
};
const statusStyle = (deboardEmp) => {
  if (deboardEmp === false) return { background: "#e8f5e9", color: "#2e7d32" };
  if (deboardEmp === true) return { background: "#ffebee", color: "#c62828" };
  return { background: "#f3f3f3", color: "#555" };
};

// ─── Stepper ─────────────────────────────────────────────────────────────────
const stepLabels = ["Personal Details", "Communication Details", "Service Details", "Bank Details", "Documents"];

const ModalStepper = ({ activeStep, onStepClick, lockedUserInfoId }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: 20, width: "100%" }}>
    {stepLabels.map((label, index) => {
      const isCompleted = index < activeStep;
      const isActive = index === activeStep;
      return (
        <React.Fragment key={index}>
          <div
            style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", flexShrink: 0 }}
            onClick={() => {
              if (index === 4 && !lockedUserInfoId) {
                toast.error("Please save employee before uploading documents");
                return;
              }
              onStepClick(index);
            }}
          >
            {/* Circle */}
            <div style={{
              width: 26, height: 26, borderRadius: "50%",
              background: isCompleted || isActive ? "#3b5fc0" : "#e8eaf6",
              color: isCompleted || isActive ? "#fff" : "#9e9e9e",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700, flexShrink: 0,
            }}>
              {isCompleted
                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                : index + 1}
            </div>
            {/* Label — only show for active and completed steps */}
            <span style={{
              fontSize: 11,
              fontWeight: isActive ? 600 : 400,
              color: isActive || isCompleted ? "#3b5fc0" : "#9e9e9e",
              whiteSpace: "nowrap",
              maxWidth: isActive ? 120 : isCompleted ? 90 : 0,
              overflow: "hidden",
              transition: "max-width 0.2s",
              display: "inline-block",
            }}>
              {label}
            </span>
          </div>
          {/* Connector */}
          {index < stepLabels.length - 1 && (
            <div style={{
              flex: 1, height: 2,
              background: index < activeStep ? "#3b5fc0" : "#e0e0e0",
              margin: "0 4px",
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Modal Shell ──────────────────────────────────────────────────────────────
const AddEmployeeModal = ({ show, onClose, children, title }) => {
  if (!show) return null;
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1050,
        background: "rgba(0,0,0,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
    >
      <div style={{
        background: "#fff",
        borderRadius: 12,
        width: "100%",
        maxWidth: 580,
        maxHeight: "90vh",
        overflowY: "auto",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        padding: "20px 24px 20px",
        position: "relative",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h5 style={{ fontWeight: 700, fontSize: 17, margin: 0, color: "#1a1a2e" }}>{title}</h5>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 18, lineHeight: 1, padding: 4 }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const EmpInfo = () => {
  const [users, setUsers] = useState([]);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [lockedUserInfoId, setLockedUserInfoId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touchedStep, setTouchedStep] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [formResetKey, setFormResetKey] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [listDept, setListDept] = useState("All Departments");
  const [listStatus, setListStatus] = useState("All Status");

  // ✅ FIX: useRef to always have the latest activeStep value inside async functions
  const activeStepRef = useRef(activeStep);
  useEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep]);

  // Snapshot of the employee's data at the moment Edit was opened, used to
  // detect whether the user actually changed anything before saving.
  const originalEmployeeDataRef = useRef(null);

  const [employeeData, setEmployeeData] = useState({
    personalDetails: {},
    communicationDetails: {},
    bankDetails: {},
    serviceDetails: {},
    documents: [],
    photo: null,
    photoPreview: null,
  });

  // ── Validators ────────────────────────────────────────────────────────────
  const validatePersonalDetails = () => {
    const errs = {};
    const p = employeeData.personalDetails || {};
    const firstV = p.firstName || "";
    if (!firstV) errs.firstName = "First Name is required";
    else if (!firstV.trim()) errs.firstName = "First Name cannot contain space only.";
    else if (!/^[A-Za-z]+$/.test(firstV.trim())) errs.firstName = "First Name must contain only alphabets";
    const lastV = p.lastName || "";
    if (!lastV) errs.lastName = "Last Name is required";
    else if (!lastV.trim()) errs.lastName = "Last Name cannot contain space only.";
    else if (!/^[A-Za-z]+$/.test(lastV.trim())) errs.lastName = "Last Name must contain only alphabets";
if (!p.gender) errs.gender = "Gender is required";
if (!p.dob) errs.dob = "Date of Birth is required";
const isEditMode = !!(lockedUserInfoId || employeeData.userInfoId);
if (!isEditMode && !employeeData.photo && !employeeData.photoPreview) errs.photo = "Photo is required";    const midV = p.middleName || "";
    if (midV && midV.trim() && !/^[A-Za-z]+$/.test(midV.trim())) errs.middleName = "Middle Name must contain only alphabets";
    const aadV = p.aadharNo || "";
    if (aadV) {
      const t = aadV.trim();
      if (!t) errs.aadharNo = "Aadhaar No cannot be spaces only";
      else if (!/^\d+$/.test(t)) errs.aadharNo = "Aadhaar No must contain only digits";
      else if (t.length !== 12) errs.aadharNo = "Aadhaar No must be exactly 12 digits";
    }
    return errs;
  };

  const isDuplicateEmail = (email, excludeUserInfoId) => {
    const normalized = (email || "").trim().toLowerCase();
    if (!normalized) return false;
    return users.some(
      (u) =>
        (u.email || "").trim().toLowerCase() === normalized &&
        u.userInfoId !== excludeUserInfoId
    );
  };

  const validateCommunicationDetails = () => {
    const errs = {};
    const c = employeeData.communicationDetails || {};
    const emailRegex = /^(?!\.)(?!.*\.\.)[A-Za-z0-9._%+-]+(?<!\.)@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
    const emailV = c.email || "";
    const currentUserInfoId = lockedUserInfoId || employeeData.userInfoId || null;
    if (!emailV) errs.email = "Email is required";
    else if (/\s/.test(emailV)) errs.email = "Email cannot contain spaces";
    else if (!emailRegex.test(emailV.trim())) errs.email = "Enter a valid email address";
    else if (isDuplicateEmail(emailV, currentUserInfoId)) errs.email = "This email is already registered with another employee";
    const mobileV = c.mobile || "";
    if (!mobileV) errs.mobile = "Mobile number is required";
    else if (!/^[0-9]+$/.test(mobileV.trim())) errs.mobile = "Mobile number must contain only digits";
    else if (mobileV.trim().length !== 10) errs.mobile = "Mobile number must be exactly 10 digits";
   if (!c.currentAddress) errs.currentAddress = "Address is required";
else if (!c.currentAddress.trim()) errs.currentAddress = "Address cannot contain only spaces";
if (!c.city) errs.city = "City is required";
if (!c.state) errs.state = "State is required";
return errs;
  };

 const validateServiceDetails = () => {
    const errs = {};
    const s = employeeData.serviceDetails || {};
    if (!s.employeeId && !employeeData.personalDetails?.employeeId) errs.employeeId = "Employee ID is required";
    if (!s.departmentId) errs.departmentId = "Department is required";
    if (!s.designationId) errs.designationId = "Designation is required";
    if (!s.appointId) errs.appointId = "Appointment type is required";
    if (!s.dateOfJoining) errs.dateOfJoining = "Date of Joining is required";
    return errs;
  };

const validateBankDetails = () => {
    const errs = {};
    const b = employeeData.bankDetails || {};
    const accV = b.accountNo || "";
    if (accV) {
      const t = accV.trim();
      if (!t) errs.accountNo = "Account number cannot contain only spaces";
      else if (/\s/.test(accV)) errs.accountNo = "Account number cannot contain spaces";
      else if (!/^[0-9]+$/.test(t)) errs.accountNo = "Account number must contain only digits";
      else if (t.length < 8) errs.accountNo = "Account number must be at least 8 digits";
      else if (t.length > 20) errs.accountNo = "Account number cannot be more than 20 digits";
    }
    const panV = b.pan_No || "";
    if (panV) {
      const t = panV.trim();
      if (!t) errs.pan_No = "PAN cannot contain only spaces";
      else if (/\s/.test(panV)) errs.pan_No = "PAN cannot contain spaces";
      else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(t)) errs.pan_No = "Enter valid PAN (Example: ABCDE1234F)";
    }
    const uanV = b.uan_No || "";
    if (uanV) {
      const t = uanV.trim();
      if (!t) errs.uan_No = "UAN cannot contain only spaces";
      else if (/\s/.test(uanV)) errs.uan_No = "UAN cannot contain spaces";
      else if (!/^[0-9]+$/.test(t)) errs.uan_No = "UAN must contain only digits";
      else if (t.length !== 12) errs.uan_No = "UAN must be exactly 12 digits";
    }
    return errs;
  };

  const validatePersonalField = (name, value) => {
    if (["firstName", "lastName", "middleName"].includes(name) && value && !/^[A-Za-z]+$/.test(value)) return "Only alphabets allowed";
    if (name === "aadharNo") {
      if (value && /\s/.test(value)) return "Spaces are not allowed";
      if (value && !/^\d+$/.test(value)) return "Only numbers are allowed";
      if (value && value.length !== 12) return "Aadhaar must be exactly 12 digits";
    }
    return "";
  };

const validatePhoto = () => {
  const isEditMode = !!(lockedUserInfoId || employeeData.userInfoId);
  if (isEditMode) return "";
  return !employeeData.photo && !employeeData.photoPreview ? "Photo is required" : "";
};
  const validateCommunicationField = (name, value) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (name === "email" && value && !emailPattern.test(value)) return "Not a valid email";
    if (name === "mobile") {
      if (value && !/^\d+$/.test(value)) return "Only numbers are allowed";
      if (value && value.length !== 10) return "Mobile number must be exactly 10 digits";
    }
    return "";
  };

  // ── Data fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("token");
    getAllEmployees(token)
      .then((res) => setUsers(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const resetForm = () => {
    setEmployeeData({ personalDetails: {}, communicationDetails: {}, bankDetails: {}, serviceDetails: {}, documents: [], photo: null, photoPreview: null });
    setErrors({});
    setTouchedStep({});
    setTouchedFields({});
    setActiveStep(0);
    setFormResetKey((p) => p + 1);
    originalEmployeeDataRef.current = null; // clear stale snapshot from any previous edit session
  };

  const handleEmployeeSelect = (emp) => {
      console.log("Employee API data:", JSON.stringify(emp)); 

    setSelectedEmployee(emp);
    setLockedUserInfoId(emp.userInfoId);
    setErrors({});

    const initialData = {
personalDetails: { employeeId: emp.emp_Id, firstName: emp.firstName, middleName: emp.middleName, lastName: emp.lastName, dob: emp.dob, gender: emp.gender, aadharNo: emp.aadharNo },communicationDetails: { 
  currentAddress: emp.currentAddress, 
  email: emp.email, 
  mobile: emp.mobile, 
  state: emp.state || "", 
  city: emp.city || "",
  _cityFromServer: emp.city || "",   // preserve original city even if state is null
},      bankDetails: { bankName: emp.bankName, bankId: emp.bankId, accountNo: emp.accountNo, uan_No: emp.uan_No, pan_No: emp.pan_No, ifscCode: emp.ifscCode, branchName: emp.branchName, accountHolderName: emp.accountHolderName },
serviceDetails: { departmentId: emp.departmentId, designationId: emp.designationId, appointId: emp.appointId, dateOfJoining: emp.dateOfJoining, dateOfReleaving: emp.dateOfReleaving, status: emp.deboardEmp === true ? "inactive" : "active" },      documents: emp.documents || [],
      photo: null,
      photoPreview: emp.photoUrl || null,
      userInfoId: emp.userInfoId,
    };

    setEmployeeData(initialData);

    // Snapshot only the editable sections — photo/documents/preview excluded
    originalEmployeeDataRef.current = JSON.stringify({
      personalDetails: initialData.personalDetails,
      communicationDetails: initialData.communicationDetails,
      bankDetails: initialData.bankDetails,
      serviceDetails: initialData.serviceDetails,
    });
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const handleNext = () => {
    if (activeStep === 0) {
      const stepErrors = { ...validatePersonalDetails() };
      const photoErr = validatePhoto();
      if (photoErr) stepErrors.photo = photoErr;
      if (Object.keys(stepErrors).length > 0) { setErrors(stepErrors); setTouchedStep((p) => ({ ...p, 0: true })); return; }
    }
    if (activeStep === 1) {
  const stepErrors = validateCommunicationDetails();
  const isEditMode = !!(lockedUserInfoId || employeeData.userInfoId);
  const filteredErrors = isEditMode
    ? Object.fromEntries(Object.entries(stepErrors).filter(([k]) => k !== "state" && k !== "city"))
    : stepErrors;
  if (Object.keys(filteredErrors).length > 0) { 
    setErrors(filteredErrors); 
    setTouchedStep((p) => ({ ...p, 1: true })); 
    return; 
  }
}
    if (activeStep === 2) {
      const stepErrors = validateServiceDetails();
      if (Object.keys(stepErrors).length > 0) { setErrors(stepErrors); setTouchedStep((p) => ({ ...p, 2: true })); return; }
    }
    if (activeStep === 3) {
      const stepErrors = validateBankDetails();
      if (Object.keys(stepErrors).length > 0) { setErrors(stepErrors); setTouchedStep((p) => ({ ...p, 3: true })); return; }
    }
    setErrors({});
    setActiveStep((p) => Math.min(p + 1, stepLabels.length - 1));
  };

  const handleBack = () => setActiveStep((p) => Math.max(p - 1, 0));

  // ── Submit / Save ─────────────────────────────────────────────────────────
  // ✅ FIX: Accept a `navigateToDocuments` flag so the caller controls navigation
  // This avoids the stale closure bug where activeStep inside handleSubmit
  // was always the value from when the function was created, not the current one.
  const handleSubmit = async (deboard = false, navigateToDocuments = false) => {
    const existingId = lockedUserInfoId || employeeData.userInfoId || null;
    const isUpdate = !!existingId;

    // If editing and nothing actually changed (no field edits, no new photo,
    // no manual deboard action), skip the API call and the success toast.
    if (isUpdate && !deboard && !employeeData.photo && originalEmployeeDataRef.current) {
      const currentSnapshot = JSON.stringify({
        personalDetails: employeeData.personalDetails,
        communicationDetails: employeeData.communicationDetails,
        bankDetails: employeeData.bankDetails,
        serviceDetails: employeeData.serviceDetails,
      });
      if (currentSnapshot === originalEmployeeDataRef.current) {
        toast.info("No changes to update");
        if (navigateToDocuments) setActiveStep(4);
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem("token");
      const formData = new FormData();

      const payload = {
        ...(existingId && { userInfoId: existingId }),
        emp_Id: employeeData.serviceDetails?.employeeId || employeeData.personalDetails?.employeeId,
        firstName: employeeData.personalDetails.firstName,
        middleName: employeeData.personalDetails.middleName,
        lastName: employeeData.personalDetails.lastName,
        dob: employeeData.personalDetails.dob,
        gender: employeeData.personalDetails.gender,
        aadharNo: employeeData.personalDetails.aadharNo,
        currentAddress: employeeData.communicationDetails.currentAddress,
        email: employeeData.communicationDetails.email,
        mobile: employeeData.communicationDetails.mobile,
        state: employeeData.communicationDetails.state,
city: employeeData.communicationDetails.city,
        bankId: employeeData.bankDetails.bankId,
        accountHolderName: employeeData.bankDetails.accountHolderName,
        accountNo: employeeData.bankDetails.accountNo,
        ifscCode: employeeData.bankDetails.ifscCode,
        branchName: employeeData.bankDetails.branchName,
        uan_No: employeeData.bankDetails.uan_No,
        pan_No: employeeData.bankDetails.pan_No,
        dateOfJoining: employeeData.serviceDetails.dateOfJoining,
        dateOfReleaving: employeeData.serviceDetails.dateOfReleaving,
        departmentId: employeeData.serviceDetails.departmentId,
        designationId: employeeData.serviceDetails.designationId,
        appointId: employeeData.serviceDetails.appointId,
        deboardEmp: deboard || employeeData.serviceDetails?.status === "inactive",
      };

      console.log("FULL PAYLOAD BEING SENT:", JSON.stringify(payload, null, 2));
formData.append("employee", new Blob([JSON.stringify(payload)], { type: "application/json" }));
      if (employeeData.photo) formData.append("photo", employeeData.photo);

      const response = await submitEmployee(formData, token);
      console.log("Save response:", response.data);

      const savedId = response.data.userInfoId;
      setLockedUserInfoId(savedId);
      setEmployeeData((prev) => ({ ...prev, userInfoId: savedId }));

      toast.success(isUpdate ? "Employee updated successfully!" : "Employee saved successfully!");

      const res = await getAllEmployees(token);
      setUsers(res.data);
      console.log("userinfo:", res.data);

      // ✅ FIX: Use the navigateToDocuments flag passed by the caller
      // instead of checking stale activeStep from closure
      if (navigateToDocuments) {
        setActiveStep(4);
      }
    } catch (error) {
      console.error("Submit Error:", error?.response?.data || error);
      const msg = error?.response?.data?.message || error?.response?.data || "Error while saving employee";
      toast.error(typeof msg === "string" ? msg : "Error while saving employee");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Form open/close ───────────────────────────────────────────────────────
  const openAddForm = () => {
    setSelectedEmployee(null);
    setLockedUserInfoId(null);
    resetForm();
    setShowModal(true);
  };

  const openEditForm = (emp) => {
    handleEmployeeSelect(emp);
    setActiveStep(0);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEmployee(null);
    setLockedUserInfoId(null);
    resetForm();
  };

  // ── List filters ─────────────────────────────────────────────────────────
  const getEmpIdNumber = (emp) => {
    const idStr = emp.emp_Id || emp.empId || "";
    const match = idStr.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
  };

  const filteredUsers = users
    .filter((emp) => {
      const q = listSearch.toLowerCase();
      const fullName = [emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.name || "";
      const matchSearch = !q || fullName.toLowerCase().includes(q) || (emp.emp_Id || emp.empId || "").toLowerCase().includes(q) || (emp.designationName || "").toLowerCase().includes(q);
      const matchDept = listDept === "All Departments" || emp.departmentName === listDept;
      const empStatus = emp.deboardEmp === false ? "active" : emp.deboardEmp === true ? "inactive" : "";
      const matchStatus = listStatus === "All Status" || empStatus === listStatus.toLowerCase();
      return matchSearch && matchDept && matchStatus;
    })
    .sort((a, b) => getEmpIdNumber(a) - getEmpIdNumber(b));
  const totalCount = users.length;
  const activeCount = users.filter((e) => e.deboardEmp === false).length;
  const inactiveCount = users.filter((e) => e.deboardEmp === true).length;
  const onLeaveCount = 0;
  const newJoinersCount = (() => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  return users.filter((e) => {
    if (!e.dateOfJoining) return false;
    const joinDate = new Date(e.dateOfJoining);
    return joinDate.getMonth() === currentMonth && joinDate.getFullYear() === currentYear;
  }).length;
})();


  // ── Step rendering ────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (activeStep) {
      case 0: return (
        <PersonalDetails
          key={formResetKey}
          employeeData={employeeData}
          setEmployeeData={setEmployeeData}
          errors={errors}
          setErrors={setErrors}
          touched={touchedStep[0]}
          validateField={validatePersonalField}
        />
      );
      case 1: return (
        <CommunicationDetails
          employeeData={employeeData}
          setEmployeeData={setEmployeeData}
          errors={errors}
          touchedFields={touchedFields}
          setTouchedFields={setTouchedFields}
          setErrors={setErrors}
          validateField={validateCommunicationField}
          existingEmployees={users}
          currentUserInfoId={lockedUserInfoId || employeeData.userInfoId || null}
        />
      );
      case 2: return (
        <ServiceDetails
          employeeData={employeeData}
          setEmployeeData={setEmployeeData}
          errors={errors}
          setErrors={setErrors}
          isSubmitting={isSubmitting}
          isEditMode={!!(lockedUserInfoId || employeeData.userInfoId)}
        />
      );
      case 3: return (
        <BankDetails
          employeeData={employeeData}
          setEmployeeData={setEmployeeData}
          errors={errors}
          setErrors={setErrors}
        />
      );
      case 4: return (
        <DocumentDetails
          userInfoId={lockedUserInfoId ?? null}
          employeeDocuments={employeeData.documents}
          onDocumentUploaded={(newDoc) => {
            setEmployeeData((prev) => ({ ...prev, documents: [...prev.documents, newDoc] }));

            setUsers((prev) =>
              prev.map((u) =>
                u.userInfoId === lockedUserInfoId
                  ? {
                      ...u,
                      documents: [
                        ...(u.documents || []).filter((d) => d.documentName !== newDoc.documentName),
                        newDoc,
                      ],
                    }
                  : u
              )
            );
          }}
        />
      );
      default: return null;
    }
  };

  // ── Footer button logic ───────────────────────────────────────────────────
  // Step 3 (Bank Details): Show "Save & Continue" → saves employee then goes to Documents
  // Step 4 (Documents): Show "Save Employee" → updates employee data
  // Steps 0-2: Show "Next"
  const renderFooterButtons = () => {
    if (activeStep === 4) {
      return (
        <>
          <button
            onClick={handleBack}
            style={{ padding: "10px 28px", borderRadius: 8, border: "1.5px solid #d0d0d0", background: "#fff", color: "#444", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
          >
            Back
          </button>
          <button
            onClick={closeModal}
            style={{ padding: "10px 28px", borderRadius: 8, border: "1.5px solid #3b5fc0", background: "#fff", color: "#3b5fc0", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
          >
            Done
          </button>
        </>
      );
    }

    if (activeStep === 3) {
      // ✅ FIX: Pass navigateToDocuments=true so handleSubmit reliably moves to step 4
      // Old code used `if (activeStep === 3)` inside handleSubmit which read a stale
      // closure value and never triggered setActiveStep(4).
      const handleSaveAndContinue = async () => {
    console.log("handleSaveAndContinue called");
    const stepErrors = validateBankDetails();
    console.log("Bank errors:", stepErrors);
    if (Object.keys(stepErrors).length > 0) {
          setErrors(stepErrors);
          setTouchedStep((p) => ({ ...p, 3: true }));
          return;
        }
        setErrors({});
        await handleSubmit(false, true); // ✅ true = navigate to Documents on success
      };

      return (
        <>
          <button
            onClick={handleBack}
            style={{ padding: "10px 28px", borderRadius: 8, border: "1.5px solid #d0d0d0", background: "#fff", color: "#444", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
          >
            Back
          </button>
          <button
            onClick={handleSaveAndContinue}
            disabled={isSubmitting}
            style={{ padding: "10px 28px", borderRadius: 8, border: "none", background: isSubmitting ? "#9e9e9e" : "#3b5fc0", color: "#fff", fontWeight: 600, fontSize: 14, cursor: isSubmitting ? "not-allowed" : "pointer" }}
          >
            {isSubmitting ? "Saving…" : "Save & Continue"}
          </button>
        </>
      );
    }

    return (
      <>
        {activeStep > 0 && (
          <button
            onClick={handleBack}
            style={{ padding: "10px 28px", borderRadius: 8, border: "1.5px solid #d0d0d0", background: "#fff", color: "#444", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
          >
            Back
          </button>
        )}
        <button
          onClick={handleNext}
          style={{ padding: "10px 28px", borderRadius: 8, border: "none", background: "#3b5fc0", color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
        >
          Next
        </button>
      </>
    );
  };
  // ── LIST VIEW ─────────────────────────────────────────────────────────────
  return (
    <Container
      fluid
      className="p-4"
      style={{
        height: "calc(100vh - 62px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Row className="mb-3 align-items-center justify-content-between">
        <Col>
          <h3 className="page-main-heading">Employees</h3>
<p className="page-sub-heading">Manage and view all employees</p>
        </Col>
        <Col xs="auto">
          <Button
            style={{ background: "#3b5fc0", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}
            onClick={openAddForm}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Employee
          </Button>
        </Col>
      </Row>

      {/* Stat Cards */}
      <Row className="mb-4 g-3">
        {[
          { label: "Total Employees", value: totalCount, bg: "#e3f2fd", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="#1976d2"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg> },
          { label: "Active Employees", value: activeCount, bg: "#e8f5e9", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="#388e3c"><path d="M12 12c2.7 0 8 1.34 8 4v2H4v-2c0-2.66 5.3-4 8-4zm0-2a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" /></svg> },
          { label: "Inactive Employees", value: inactiveCount, bg: "#ffebee", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="#c62828"><path d="M14 8c0-2.21-1.79-4-4-4S6 5.79 6 8s1.79 4 4 4 4-1.79 4-4zm3 2v2h6v-2h-6zM2 18v2h16v-2c0-2.66-5.33-4-8-4s-8 1.34-8 4z" /></svg> },
          { label: "On Leave", value: onLeaveCount, bg: "#fff3e0", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="#f57c00"><path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" /></svg> },
          { label: "New Joiners", value: newJoinersCount, bg: "#fce4ec", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="#c2185b"><path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg> },
        ].map((stat, i) => (
          <Col key={i} xs={12} sm={6} md>
            <Card className="h-100" style={{ border: "0.5px solid #e0e0e0", borderRadius: 12, boxShadow: "none" }}>
              <Card.Body style={{ display: "flex", alignItems: "center", gap: 16, padding: "20px" }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: stat.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{stat.icon}</div>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 500, color: "#212121", lineHeight: 1.2 }}>{stat.value}</div>
                  <div style={{ fontSize: 13, color: "#888" }}>{stat.label}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Row className="mb-3 g-2 align-items-center">
        <Col xs={12} md={5}>
          <InputGroup>
            <InputGroup.Text style={{ background: "#fff", border: "0.5px solid #ddd", borderRight: "none" }}>
              <FontAwesomeIcon icon={faSearch} style={{ color: "#aaa", fontSize: 13 }} />
            </InputGroup.Text>
            <Form.Control style={{ border: "0.5px solid #ddd", borderLeft: "none", fontSize: 14, height: 38 }} placeholder="Search by name, ID, or role..." value={listSearch} onChange={(e) => setListSearch(e.target.value)} />
          </InputGroup>
        </Col>
        <Col xs="auto">
          <Form.Select style={{ height: 38, fontSize: 14, border: "0.5px solid #ddd", borderRadius: 8 }} value={listDept} onChange={(e) => setListDept(e.target.value)}>
            <option>All Departments</option>
            {[...new Set(users.map((u) => u.departmentName).filter(Boolean))].map((d) => <option key={d}>{d}</option>)}
          </Form.Select>
        </Col>
        <Col xs="auto">
          <Form.Select style={{ height: 38, fontSize: 14, border: "0.5px solid #ddd", borderRadius: 8 }} value={listStatus} onChange={(e) => setListStatus(e.target.value)}>
            <option>All Status</option>
            <option>Active</option>
            <option>On Leave</option>
            <option>Inactive</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Table */}
      <Card style={{ border: "0.5px solid #e0e0e0", borderRadius: 12, overflow: "hidden", boxShadow: "none", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f9f9f9" }}>
                {["Employee", "Employee ID", "Department", "Designation", "Type", "Status", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 500, fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "0.5px solid #e0e0e0", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2, background: "#f9f9f9" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#aaa", fontSize: 14 }}>No employees found.</td></tr>
              ) : filteredUsers.map((emp) => {
                const av = avatarStyle();
                const st = statusStyle(emp.deboardEmp);
                return (
                  <tr key={emp.userInfoId} style={{ borderBottom: "0.5px solid #f0f0f0" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ padding: "14px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0, border: "1px solid #2E5CB840" }}>
                          {initials([emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.name)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>
                            {[emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.name || "—"}
                          </div>
                          <div style={{ fontSize: 12, color: "#888" }}>{emp.email || "—"}</div>
                        </div>
                      </div>
                    </td>
<td style={{ padding: "14px 16px" }}>{emp.emp_Id || emp.empId || "—"}</td>
                    <td style={{ padding: "14px 16px" }}>{emp.departmentName || "—"}</td>
                    <td style={{ padding: "14px 16px" }}>{emp.designationName || "—"}</td>
                    <td style={{ padding: "14px 16px" }}>{emp.appointName || "Full-time"}</td>
                    <td style={{ padding: "14px 16px" }}>
<span style={{ ...st, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500 }}>{emp.deboardEmp === false ? "Active" : "Inactive"}</span>                    </td>
                    <td style={{ padding: "14px 16px" }}>
<button title="View" style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: "4px 6px", borderRadius: 4, fontSize: 15, marginRight: 4 }} onClick={() => setViewEmployee(emp)}><FontAwesomeIcon icon={faEye} /></button>                      <button title="Edit" style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: "4px 6px", borderRadius: 4, fontSize: 15 }} onClick={() => openEditForm(emp)}><FontAwesomeIcon icon={faPen} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── MODAL ─────────────────────────────────────────────────────────── */}
<AddEmployeeModal show={showModal} onClose={closeModal} title={selectedEmployee ? "Update Employee Details" : "Add New Employee"}><ModalStepper activeStep={activeStep} onStepClick={(targetStep) => {
  // Only allow going backwards freely; going forward requires passing validation
  if (targetStep < activeStep) {
    setActiveStep(targetStep);
    return;
  }
  // Validate each step between current and target
  for (let step = activeStep; step < targetStep; step++) {
    if (step === 0) {
      const errs = { ...validatePersonalDetails() };
      const photoErr = validatePhoto();
      if (photoErr) errs.photo = photoErr;
      if (Object.keys(errs).length > 0) { setErrors(errs); setTouchedStep((p) => ({ ...p, 0: true })); return; }
    }
    if (step === 1) {
  const errs = validateCommunicationDetails();
  // In edit mode, skip state/city validation if they're empty due to old data
  const isEditMode = !!(lockedUserInfoId || employeeData.userInfoId);
  const filteredErrs = isEditMode
    ? Object.fromEntries(Object.entries(errs).filter(([k]) => k !== "state" && k !== "city"))
    : errs;
  if (Object.keys(filteredErrs).length > 0) { setErrors(filteredErrs); setTouchedStep((p) => ({ ...p, 1: true })); return; }
}
    if (step === 2) {
      const errs = validateServiceDetails();
      if (Object.keys(errs).length > 0) { setErrors(errs); setTouchedStep((p) => ({ ...p, 2: true })); return; }
    }
    if (step === 3) {
      const errs = validateBankDetails();
      if (Object.keys(errs).length > 0) { setErrors(errs); setTouchedStep((p) => ({ ...p, 3: true })); return; }
    }
  }
  setErrors({});
  setActiveStep(targetStep);
}} lockedUserInfoId={lockedUserInfoId} />
        <div style={{ minHeight: 260 }}>
          {renderStep()}
        </div>

        {/* Footer buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, paddingTop: 14, borderTop: "1px solid #f0f0f0" }}>
          {renderFooterButtons()}
        </div>
      </AddEmployeeModal>
<ViewEmployeeModal show={!!viewEmployee} onClose={() => setViewEmployee(null)} emp={viewEmployee} />
      <ToastContainer position="top-right" autoClose={3000} />
    </Container>
  );
};
// ─── View Employee Modal ──────────────────────────────────────────────────────
const ViewEmployeeModal = ({ show, onClose, emp }) => {
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    if (!show || !emp?.userInfoId) { setPhotoUrl(null); return; }
    const token = localStorage.getItem("token");
    let objectUrl;
    getEmployeePhoto(emp.userInfoId, token)
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setPhotoUrl(objectUrl);
      })
      .catch(() => setPhotoUrl(null));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [show, emp?.userInfoId]);

  if (!show || !emp) return null;

  const fullName = [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ") || emp.name || "—";
  const status = emp.deboardEmp === false ? "Active" : "Inactive";
  const statusColor = emp.deboardEmp === false ? { background: "#e8f5e9", color: "#2e7d32" } : { background: "#ffebee", color: "#c62828" };

  const Field = ({ label, value }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, color: "#1a202c", fontWeight: 500 }}>{value || "—"}</div>
    </div>
  );

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#3b5fc0", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12, paddingBottom: 6, borderBottom: "1.5px solid #e8eaf6" }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 24px" }}>{children}</div>
    </div>
  );

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1050, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 580, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", padding: "20px 24px", position: "relative" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h5 style={{ fontWeight: 700, fontSize: 17, margin: 0, color: "#1a1a2e" }}>Employee Details</h5>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 18, lineHeight: 1, padding: 4 }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Profile header */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 16px", background: "#f8fafc", borderRadius: 10, marginBottom: 20, border: "1px solid #e8eaf6" }}>
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={fullName}
              style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "1.5px solid #2E5CB840" }}
            />
          ) : (
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#2E5CB81A", color: "#2E5CB8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, flexShrink: 0, border: "1.5px solid #2E5CB840" }}>
              {fullName.trim().split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("")}
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>{fullName}</div>
            <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>{emp.email || "—"}</div>
            <div style={{ fontSize: 12, color: "#888" }}>{emp.designationName || "—"} {emp.departmentName ? `· ${emp.departmentName}` : ""}</div>
          </div>
          <span style={{ ...statusColor, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{status}</span>
        </div>

        {/* Personal */}
        <Section title="Personal Details">
          <Field label="First Name" value={emp.firstName} />
          <Field label="Last Name" value={emp.lastName} />
          <Field label="Middle Name" value={emp.middleName} />
          <Field label="Gender" value={emp.gender ? emp.gender.charAt(0).toUpperCase() + emp.gender.slice(1) : "—"} />
          <Field label="Date of Birth" value={emp.dob} />
        </Section>

        {/* Communication */}
        <Section title="Communication Details">
          <Field label="Email" value={emp.email} />
          <Field label="Mobile" value={emp.mobile} />
          <Field label="Address" value={emp.currentAddress} />
          <Field label="State" value={emp.state} />
          <Field label="City" value={emp.city} />
        </Section>

        {/* Service */}
        <Section title="Service Details">
          <Field label="Employee ID" value={emp.emp_Id} />
          <Field label="Department" value={emp.departmentName} />
          <Field label="Designation" value={emp.designationName} />
          <Field label="Employment Type" value={emp.appointName} />
          <Field label="Date of Joining" value={emp.dateOfJoining} />
          <Field label="Date of Relieving" value={emp.dateOfReleaving} />
        </Section>

        {/* Bank */}
        <Section title="Bank Details">
          <Field label="Bank Name" value={emp.bankName} />
          <Field label="Account No" value={emp.accountNo} />
          <Field label="IFSC Code" value={emp.ifscCode} />
          <Field label="Branch" value={emp.branchName} />
          <Field label="PAN No" value={emp.pan_No} />
          <Field label="UAN No" value={emp.uan_No} />
        </Section>

        {/* Documents */}
        {emp.documents && emp.documents.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#3b5fc0", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12, paddingBottom: 6, borderBottom: "1.5px solid #e8eaf6" }}>
              Documents
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {emp.documents.map((doc, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "12px 16px", background: "#f8fafc" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#1a202c" }}>{doc.documentName || "—"}</div>
                    <div style={{ fontSize: 12, color: "#16a34a", marginTop: 2 }}>✓ {doc.fileName || "Uploaded"}</div>
                  </div>
                  <button
                    onClick={async () => {
  try {
    const token = localStorage.getItem("token");
    const res = await downloadEmpDocument(doc.empDocumentId, token);
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", doc.fileName || "document");
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        console.error("Download failed", err);
                      }
                    }}
                    style={{ padding: "7px 16px", borderRadius: 8, border: "1.5px solid #3b5fc0", background: "#fff", color: "#3b5fc0", fontWeight: 600, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 14, borderTop: "1px solid #f0f0f0" }}>
          <button onClick={onClose} style={{ padding: "10px 28px", borderRadius: 8, border: "1.5px solid #3b5fc0", background: "#fff", color: "#3b5fc0", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmpInfo;